import type { EvidenceDispositionCode, EvidenceItem, ReconciliationItem } from './types';

export interface EvidenceUsageState {
  evidence: readonly EvidenceItem[];
  reconciliation: readonly ReconciliationItem[];
}

/**
 * Returns only proposals whose source is still part of the active tax record.
 * A proposal excluded on its own remains visible as "not used"; removing its
 * entire source hides it from active workflows while retaining the audit data.
 */
export function activeReconciliationForEvidence(evidence: readonly EvidenceItem[],
  reconciliation: readonly ReconciliationItem[]): ReconciliationItem[] {
  const removedEvidenceIds = new Set(evidence
    .filter((item) => item.state === 'unlinked' || item.state === 'superseded')
    .map((item) => item.id));
  return reconciliation.filter((item) => !item.evidenceId || !removedEvidenceIds.has(item.evidenceId));
}

/**
 * Removes one source from active tax use while retaining its immutable custody
 * record and the source-derived proposals needed for an audit trail.
 */
export function unlinkEvidenceUsage<T extends EvidenceUsageState>(state: T, evidenceId: string, input: {
  reason: EvidenceDispositionCode;
  note?: string;
  actor?: string;
  recordedAt?: string;
}): T {
  const source = state.evidence.find((item) => item.id === evidenceId);
  if (!source) return state;
  const reason = dispositionLabel(input.reason);
  const evidence = state.evidence.map((item) => item.id === evidenceId ? {
    ...item,
    state: 'unlinked' as const,
    issue: `Not used in this tax record: ${reason}.`,
    disposition: {
      code: input.reason,
      ...(input.note?.trim() ? { note: input.note.trim() } : {}),
      recordedAt: input.recordedAt ?? new Date().toISOString(),
      recordedBy: input.actor ?? 'Current user',
    },
  } : item);
  const reconciliation = state.reconciliation.map((item) => item.evidenceId === evidenceId ? {
    ...item,
    state: 'excluded-by-human' as const,
    dispositionReason: reason,
  } : item);
  return { ...state, evidence, reconciliation };
}

/** Removes a successfully purged source from the browser projection. The
 * durable service retains only its deletion audit tombstone, so neither the
 * source nor its derived proposals belong in a recovered tax workspace. */
export function purgeEvidenceUsage<T extends EvidenceUsageState>(state: T, evidenceId: string): T {
  return { ...state,
    evidence: state.evidence.filter((item) => item.id !== evidenceId),
    reconciliation: state.reconciliation.filter((item) => item.evidenceId !== evidenceId) };
}

export function dispositionLabel(code: EvidenceDispositionCode): string {
  const labels: Record<EvidenceDispositionCode, string> = {
    'wrong-taxpayer': 'wrong taxpayer',
    'wrong-period': 'wrong filing period',
    'wrong-transaction': 'wrong transaction',
    unrelated: 'not relevant to this tax record',
    duplicate: 'duplicate source',
  };
  return labels[code];
}
